import { Permission } from "@/enums/permission";
import { Module } from "@/enums/modules";
import { LOCALE } from "@/enums/locale";
import { NOTIFICATION_KEYS } from "@/enums/notifications";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import cuid from "cuid";
import { store } from "@/store/store";
import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";

export const languages = [
  {
    name: "Polish",
    icon: "pl",
    locale: LOCALE.PL
  },
  {
    name: "English",
    icon: "en",
    locale: LOCALE.EN
  },
  {
    name: "Spanish",
    icon: "es",
    locale: LOCALE.ES
  }
];

export const sidebarMenu = {
  "message.common.general": [
    // Dashboard
    {
      id: cuid(),
      action: "icon-dashboard",
      group: "dashboard",
      active: true,
      path: "/dashboard",
      exact: true,
      module: Module.DASHBOARD,
      permission: Permission.VIEW_DASHBOARD
    },

    // Position monitoring
    {
      id: cuid(),
      action: "icon-eye",
      group: "monitoring",
      active: false,
      module: Module.POSITION_MONITORING,
      items: [
        {
          id: cuid(),
          path: "/monitoring/all-locations",
          exact: true,
          module: Module.POSITION_ALL_LOCATIONS,
          permission: Permission.VIEW_GMB_INSIGHTS,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_VIEW_SEO_LOCAL_SEARCH_POSITIONS
        },
        {
          id: cuid(),
          path: "/monitoring/single-location",
          exact: true,
          module: Module.POSITION_SINGLE_LOCATION,
          permission: Permission.VIEW_MAPS_RANKINGS,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_VIEW_SEO_LOCAL_SEARCH_POSITIONS
        },
        {
          id: cuid(),
          path: "/monitoring/competition-visibility",
          exact: true,
          module: Module.POSITION_COMPETITION_COMPARISON,
          permission: Permission.VIEW_MAPS_COMPETITION_RANKINGS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_COMPARE_COMPETITION
        }
      ]
    },

    // Users behavior
    {
      id: cuid(),
      action: "icon-competition",
      path: "/users-behavior",
      exact: true,
      active: false,
      module: Module.USERS_BEHAVIOR,
      permission: Permission.VIEW_GMB_INSIGHTS,
      planRestrictions: PLAN_RESTRICTIONS.CAN_MONITOR_GMB_ACTIONS_AND_REPORTS
    },

    // Business addresses
    {
      id: cuid(),
      action: "icon-marker",
      group: "business-addresses",
      active: false,
      module: Module.LOCATIONS,
      items: [
        {
          id: cuid(),
          path: "/business-addresses/list",
          exact: true,
          module: Module.LOCATIONS_MANAGE,
          permission: Permission.VIEW_FACILITIES_DATA
        },
        {
          id: cuid(),
          path: "/business-addresses/quality",
          exact: true,
          module: Module.LOCATIONS_QUALITY,
          permission: Permission.VIEW_FACILITIES_DATA,
          featureFlag: FEATURE_FLAG.CN_6145_FACILITIES_QUALITY
        }
      ]
    },

    // External changes
    {
      id: cuid(),
      action: "icon-external-changes",
      group: "external-changes",
      active: false,
      module: Module.EXTERNAL_CHANGES,
      planRestrictions: PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA,
      items: [
        {
          id: cuid(),
          path: "/external-changes/list",
          exact: true,
          module: Module.EXTERNAL_CHANGES_LIST,
          permission: Permission.VIEW_EXTERNAL_CHANGES,
          planRestrictions: PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA
        },
        {
          id: cuid(),
          path: "/external-changes/protect",
          exact: true,
          module: Module.EXTERNAL_CHANGES_PROTECT,
          permission: Permission.PROTECT_FROM_EXTERNAL_CHANGES,
          planRestrictions: PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA
        },
        {
          id: cuid(),
          path: "/external-changes/history",
          exact: true,
          module: Module.EXTERNAL_CHANGES_HISTORY,
          permission: Permission.VIEW_EXTERNAL_CHANGES_HISTORY,
          planRestrictions: PLAN_RESTRICTIONS.CAN_PROTECT_LOCATION_DATA
        }
      ]
    },

    // Photos
    {
      id: cuid(),
      action: "icon-image",
      group: "photo",
      path: "/photos",
      exact: true,
      active: false,
      module: Module.PHOTOS,
      permission: Permission.VIEW_PHOTOS,
      planRestrictions: PLAN_RESTRICTIONS.CAN_POST_PHOTOS
    },

    // Posts
    {
      id: cuid(),
      action: "icon-posts",
      group: "posts",
      path: "/posts",
      exact: true,
      active: false,
      module: Module.POSTS,
      permission: Permission.VIEW_LOCAL_POSTS,
      planRestrictions: PLAN_RESTRICTIONS.CAN_PUBLISH_MARKETING_PUBLICATIONS
    },

    // Reviews
    {
      id: cuid(),
      action: "icon-reviews",
      group: "reviews",
      active: false,
      module: Module.REVIEWS,
      items: [
        {
          id: cuid(),
          path: "/reviews/list",
          exact: true,
          module: Module.REVIEWS_LIST,
          permission: Permission.VIEW_REVIEWS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_MONITOR_RATINGS_AND_REPORTS
        },
        {
          id: cuid(),
          path: "/reviews/analyst",
          exact: true,
          module: Module.REVIEWS_ANALYTICS,
          permission: Permission.VIEW_REVIEWS_ANALYTICS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_MONITOR_RATINGS_AND_REPORTS
        },
        {
          id: cuid(),
          path: "/reviews/categories",
          exact: true,
          module: Module.REVIEWS_CATEGORIES_ANALYTICS,
          permission: Permission.VIEW_REVIEWS_ANALYTICS,
          planRestrictions: PLAN_RESTRICTIONS.CAN_USE_SENTIMENT_ANALYSIS
        }
      ]
    },
    //Review Booster

    {
      id: cuid(),
      action: "icon-review-booster",
      group: "review-booster",
      active: false,
      module: Module.REVIEW_BOOSTER,
      featureFlag: FEATURE_FLAG.SHOW_REVIEW_BOOSTER,
      visibilityGetter: {
        path: "company/hasReviewBooster",
        expectedValue: true
      },
      items: [
        {
          id: cuid(),
          path: "/review-booster/reviews",
          exact: true,
          permission: Permission.VIEW_RB_REVIEWS,
          module: Module.REVIEW_BOOSTER_REVIEWS
        },
        {
          id: cuid(),
          path: "/review-booster/form-list",
          exact: true,
          permission: Permission.VIEW_RB_FORMS,
          module: Module.REVIEW_BOOSTER_FORM_LIST
        },
        {
          id: cuid(),
          path: "/review-booster/categories-analytics",
          exact: true,
          module: Module.REVIEWS_CATEGORIES_ANALYTICS,
          permission: Permission.VIEW_RB_REVIEWS_ANALYTICS,
          featureFlag: FEATURE_FLAG.SHOW_RB_CATEGORIES_ANALYTICS
        }
      ]
    },

    {
      id: cuid(),
      action: "icon-review-booster",
      path: "/review-booster/promo",
      exact: true,
      visibilityGetter: {
        path: "company/hasReviewBooster",
        expectedValue: false
      },
      active: false,
      module: Module.REVIEW_BOOSTER,
      featureFlag: FEATURE_FLAG.SHOW_REVIEW_BOOSTER_PROMO
    },

    {
      id: cuid(),
      action: "icon-questions-and-answers",
      active: false,
      path: "/questions-and-answers",
      exact: true,
      module: Module.QUESTIONS_AND_ANSWERS,
      permission: Permission.VIEW_Q_AND_A,
      planRestrictions: PLAN_RESTRICTIONS.CAN_USE_Q_AND_A,
      featureFlag: FEATURE_FLAG.CN_6543_QUESTIONS_AND_ANSWERS
    },

    // Permissions
    {
      id: cuid(),
      action: "icon-settings",
      path: "/permissions",
      exact: true,
      active: false,
      module: Module.TEAM,
      permission: Permission.VIEW_TEAM
    },

    // Company / settings
    {
      id: cuid(),
      action: "mdi-cogs",
      group: "company",
      path: "/company",
      active: false,
      module: Module.SETTINGS,
      notifications: [NOTIFICATION_KEYS.LOCATIONS_IMPORT_PENDING],
      items: [
        {
          id: cuid(),
          path: "/company/company-profile",
          exact: true,
          module: Module.SETTINGS_COMPANY,
          permission: Permission.VIEW_COMPANY_DATA
        },
        {
          id: cuid(),
          path: "/company/company-in-web",
          exact: true,
          module: Module.SETTINGS_KEYWORDS,
          permission: Permission.MANAGE_KEYWORDS,
          planRestrictions:
            PLAN_RESTRICTIONS.CAN_VIEW_SEO_LOCAL_SEARCH_POSITIONS
        },
        {
          id: cuid(),
          path: "/company/competition-locations",
          exact: true,
          module: Module.SETTINGS_COMPETITION_LOCATIONS,
          permission: Permission.MANAGE_COMPETITION_PLACES,
          planRestrictions: PLAN_RESTRICTIONS.CAN_COMPARE_COMPETITION
        },
        {
          id: cuid(),
          path: "/company/gmb-accounts",
          exact: true,
          module: Module.SETTINGS_GMB_ACCOUNTS,
          permission: Permission.MANAGE_GMB_ACCOUNT,
          notifications: [NOTIFICATION_KEYS.LOCATIONS_IMPORT_PENDING]
        },
        {
          id: cuid(),
          path: "/company/facebook-accounts",
          exact: true,
          module: Module.SETTINGS_FACEBOOK_ACCOUNTS,
          featureFlag: FEATURE_FLAG.SHOW_FACEBOOK_INTEGRATION
        },
        {
          id: cuid(),
          path: "/company/apple-accounts",
          exact: true,
          module: Module.SETTINGS_APPLE_ACCOUNTS,
          featureFlag: FEATURE_FLAG.CN_5526_APPLE_MAPS
        },
        {
          id: cuid(),
          path: "/company/mobile-apps",
          exact: true,
          module: Module.SETTINGS_APPS_CONFIG,
          featureFlag: FEATURE_FLAG.SHOW_APPS_CONFIG_PAGE
        },
        {
          id: cuid(),
          path: "/company/map-settings",
          exact: true,
          module: Module.SETTINGS_MAP_CONFIG,
          permission: Permission.MANAGE_GMB_ACCOUNT,
          featureFlag: FEATURE_FLAG.SHOW_INTEGRATIONS
        }
      ]
    },

    // Store Locator
    {
      id: cuid(),
      action: "icon-store-locator",
      path: "/store-locator",
      exact: true,
      active: false,
      module: Module.STORE_LOCATOR,
      permission: Permission.MANAGE_STORE_LOCATOR
    }
  ]
};
